import * as THREE from 'three';
import { i18n } from 'i18n';
import {
  BackfillRecipeDto,
  BackfillRecipeForm,
  FillType,
  PourDto,
  PourPresetDto,
  PourPresetGroupDto,
  ThroughputControlType
} from 'providers/api';
import { makeLocalDateTime } from 'utils';
import { firstSelectableRheology } from 'utils/rheology';
import {
  defaultFillAnchor,
  dischargeDatum,
  getFillCurve,
  LEGACY_BEACH_ANGLE_DEGREES
} from 'modules/project/StopeFillPage/Three/pour';
import { Model } from '../hooks/useModelState';
import { PCStope } from '../MineModel/Stopes';
import { optimiseAutoRecipeForm } from './optimiseAutoRecipeForm';

export type PlannedDate = ReturnType<typeof makeLocalDateTime>;

export interface ExistingAutoPour {
  pour: PourDto;
  recipe?: BackfillRecipeDto;
}

export interface PourPlan {
  plannedDate: PlannedDate;
  volume: number;
  pourPositionX?: number;
  pourPositionY?: number;
  pourHeight?: number;
}

export type PlanSkipKey = 'skippedNoShape' | 'skippedNoPreset';

export interface PlanResolution {
  plan?: PourPlan;
  skip?: PlanSkipKey;
}

export const positiveNumber = (value: number | undefined | null): number | undefined => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

const localDateToPourDate = (date: unknown): PlannedDate | undefined => {
  if (!date) return undefined;
  const raw = typeof date === 'string' ? date : (date as { toString?: () => string }).toString?.();
  const day = raw?.substring(0, 10);
  if (!day) return undefined;
  try {
    return makeLocalDateTime(`${day}T00:00:00`);
  } catch {
    return undefined;
  }
};

export const stopeStartPourDate = (stope: PCStope): PlannedDate | undefined => (
  localDateToPourDate(stope.data.startDate) ?? localDateToPourDate(stope.data.endDate)
);

export const zonedPourDateToLocal = (date: PourDto['intendedPourDate'] | undefined): PlannedDate | undefined => {
  if (!date) return undefined;
  const candidate = date as unknown as {
    toLocalDateTime?: () => PlannedDate;
    toString?: () => string;
  };

  if (candidate.toLocalDateTime) {
    return candidate.toLocalDateTime();
  }

  const raw = candidate.toString?.();
  const localDateTime = raw?.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)?.[0];
  return localDateTime ? makeLocalDateTime(localDateTime) : undefined;
};

export const samePlannedDate = (a: PlannedDate | undefined, b: PlannedDate | undefined): boolean => (
  !!a && !!b && (a.isEqual ? a.isEqual(b) : a.toString() === b.toString())
);

export const plusCuringDays = (date: PlannedDate, days: number | undefined): PlannedDate => {
  const hours = Math.max(0, Math.round((days ?? 0) * 24));
  return date.plusHours(hours);
};

const makeAutoPourRecipeReference = (pourPreset: PourPresetDto): string => pourPreset.name;

// Headless hydraulic recipe form for the auto/preset pour flow. Paste recipes go through
// optimiseAutoRecipeForm; this builder covers the hydraulic case with the preset's strength inputs.
const makeAutoPourRecipe = (
  model: Model,
  stopeId: string,
  pourPreset: PourPresetDto,
): BackfillRecipeForm | null => {
  const rheologyDatasetId = pourPreset.rheologyDatasetId || firstSelectableRheology(model.rheology)?.rheologyDataSetId;
  if (!rheologyDatasetId) return null;

  const common = {
    reference: makeAutoPourRecipeReference(pourPreset),
    rheologyDataSetId: rheologyDatasetId,
    warnings: [],
    spools: [],
    routeSpools: [],
    backfillPlantId: model.activeBackfillPlant?.id ?? '',
    pourPresetId: pourPreset.entityId,
    stopeId,
    isAutoRecipe: true,
  };

  if (model.project.fillType === FillType.Hydraulic) {
    return {
      ...common,
      hydraulicSpecification: {
        binderTailingsRatio: 0,
        throughput: 0,
        throughputControlType: ThroughputControlType.DryTonnage,
      },
      hydraulicOptimisedSpecification: {
        massConcentration: 0,
        settlingVelocity: 0,
        minVelocity: 0,
        maxPipePressure: 0,
      },
      hydraulicSelectedSpecification: {
        massConcentration: 0,
        settlingVelocity: 0,
        minVelocity: 0,
        maxPipePressure: 0,
      },
    };
  }

  const ucsStrength = pourPreset.ucsStrength ?? 0;
  return {
    ...common,
    pasteSpecification: {
      targetUCSStrength: ucsStrength,
      targetDays: pourPreset.curingDays ?? 0,
      throughput: 0,
      throughputControlType: ThroughputControlType.DryTonnage,
      pumpPressure: 0,
    },
    pasteOptimisedSpecification: {
      massConcentration: 0,
      binderContent: 0,
      ucsStrength,
      pumpPressure: 0,
    },
    pasteSelectedSpecification: {
      massConcentration: 0,
      binderContent: 0,
      ucsStrength,
      pumpPressure: 0,
    },
  };
};

export interface AutoRecipeBuildResult {
  form: BackfillRecipeForm;
  // The form is still usable: a best-effort spec plus a UCS-below-target warning.
  optimisationFailed: boolean;
}

// Builds the recipe form for a stope+preset, running the paste optimiser where applicable so the
// auto-created recipe carries real spec values. Returns null when preconditions are unmet (no rheology).
export const buildAutoRecipeFormResult = (
  model: Model,
  stope: PCStope,
  preset: PourPresetDto,
): AutoRecipeBuildResult | null => {
  if (model.project.fillType === FillType.Hydraulic) {
    const hydraulicForm = makeAutoPourRecipe(model, stope.data.stopeId, preset);
    return hydraulicForm ? { form: hydraulicForm, optimisationFailed: false } : null;
  }
  const rheologyDataset = model.rheology?.find((dataset) => dataset.rheologyDataSetId === preset.rheologyDatasetId)
    ?? firstSelectableRheology(model.rheology);
  if (!rheologyDataset) return null;

  const optimised = optimiseAutoRecipeForm(model, stope, preset, rheologyDataset);
  return optimised ? { form: optimised.form, optimisationFailed: optimised.failed } : null;
};

export const buildAutoRecipeForm = (
  model: Model,
  stope: PCStope,
  preset: PourPresetDto,
): BackfillRecipeForm | null => buildAutoRecipeFormResult(model, stope, preset)?.form ?? null;

export const getRecipeWarningCodes = (recipe: BackfillRecipeDto): string[] => (
  recipe.warnings?.map((warning) => warning.code).filter(Boolean) as string[] ?? []
);

export const findRecipeForPreset = (
  recipes: BackfillRecipeDto[],
  stopeId: string,
  pourPresetId: string,
): BackfillRecipeDto | undefined => {
  const activeRecipes = recipes.filter((recipe) => (
    recipe.stopeIdentifier?.stopeId === stopeId
    && !recipe.isStale
    && recipe.pourPresetDto?.entityId === pourPresetId
  ));
  return activeRecipes.find((recipe) => recipe.isAutoRecipe) ?? activeRecipes[0];
};

export const buildAutoPourByPreset = (
  pours: PourDto[],
  recipes: BackfillRecipeDto[],
): Map<string, ExistingAutoPour> => {
  const recipesById = new Map(recipes.map((recipe) => [recipe.entityId, recipe]));
  const sortedPours = [...pours].sort((a, b) => {
    const aDate = zonedPourDateToLocal(a.intendedPourDate)?.toString() ?? '';
    const bDate = zonedPourDateToLocal(b.intendedPourDate)?.toString() ?? '';
    return aDate.localeCompare(bDate);
  });

  return sortedPours.reduce<Map<string, ExistingAutoPour>>((acc, pour) => {
    const recipe = pour.recipeId ? recipesById.get(pour.recipeId) : undefined;
    // Every preset-created pour carries its own preset id (recipe pours inherit it from the recipe, rock-fill
    // pours are stamped directly), so match on that first. Fall back to the recipe's preset link for older
    // pours created before the id was stamped on the pour — pourPresetDto is only populated when the recipe is
    // genuinely linked to a preset, so we don't gate it on isAutoRecipe.
    const presetId = pour.pourPresetId ?? recipe?.pourPresetDto?.entityId;
    if (!presetId) {
      return acc;
    }

    if (!acc.has(presetId)) {
      acc.set(presetId, { pour, recipe });
    }
    return acc;
  }, new Map());
};

export const getPresetSequence = (
  stope: PCStope,
  groupsById: Map<string, PourPresetGroupDto>,
  selectedGroup?: PourPresetGroupDto | null,
): PourPresetDto[] | undefined => {
  const group = selectedGroup
    ?? (stope.data.pourPresetGroupId ? groupsById.get(stope.data.pourPresetGroupId) : undefined);
  if (!group) return undefined;

  return (group.pourPresets ?? [])
    .filter((preset): preset is PourPresetDto => !!preset?.entityId)
    .map((preset) => ({
      ...preset,
      groupId: preset.groupId || group.entityId || '',
    }));
};

/**
 * Resolves one preset to a concrete pour plan. `beachAngleDegrees` is the project's default beach slope
 * (degrees from horizontal), passed IN so this stays a pure function - auto pours take the project
 * default, resolved once per batch by the caller. Rock fill is always a level 0-degree fill.
 */
export const resolvePresetPourPlan = (
  preset: PourPresetDto,
  plannedDate: PlannedDate,
  mesh: THREE.Mesh | null,
  remainingVolume: number | undefined,
  beachAngleDegrees: number = LEGACY_BEACH_ANGLE_DEGREES,
): PlanResolution => {
  if (preset.fillRemainingHeight) {
    const volume = positiveNumber(remainingVolume);
    return volume
      ? { plan: { plannedDate, volume } }
      : { skip: 'skippedNoShape' };
  }

  const height = positiveNumber(preset.height);
  if (height) {
    if (!mesh) return { skip: 'skippedNoShape' };

    // Measured off the discharge floor datum, exactly as the fill view stacks the first slice. Nothing is
    // placed yet from this function's point of view, so the datum is the bare floor under the pipe.
    const anchor = defaultFillAnchor(mesh);
    const curve = getFillCurve(mesh, [], anchor, preset.isRockFill ? 0 : beachAngleDegrees);
    const volume = curve.volumeAt(dischargeDatum(mesh, [], anchor) + height);
    // Deliberately omit pourPositionX/Y: those would be in this mesh's coordinate frame, which differs
    // from the stope-fill view's frame. Leaving them unset makes the fill view anchor the pour at its
    // own defaultAnchorPosition (stope centre) — the same frame manual pours use, so it renders.
    return positiveNumber(volume)
      ? {
        plan: {
          plannedDate,
          volume,
          pourHeight: height,
        },
      }
      : { skip: 'skippedNoShape' };
  }

  const volume = positiveNumber(preset.volume);
  return volume
    ? { plan: { plannedDate, volume } }
    : { skip: 'skippedNoPreset' };
};

export const makeAutoPourReference = (stopeName: string, presetName: string): string => i18n._(
  'Auto - {stopeName} - {presetName}',
  { stopeName, presetName },
);
